import { useCallback, useEffect, useRef, useState } from 'react';
import { api, uploadChatImage } from '../lib/api';
import { mapMessage } from '../lib/chatMappers';

/**
 * The server allows ONE message per second per user+thread. See
 * `messageRateLimiter` in server/src/modules/chat/chat.controller.ts:30-42 -
 * `windowMs: 1000, max: 1`, keyed by `${userId}:${threadId}`; it answers a
 * second message inside the same second with 429, `Retry-After: 1` and
 * `RateLimit-Reset: 1` (it advertises `RateLimit-Policy: 1;w=1`).
 *
 * Several attachments become several messages, so a batch has to space its
 * POSTs by at least that window. The small margin means a request is never
 * knowingly fired into a closed window.
 */
const MESSAGE_MIN_GAP_MS = 1100;

/** A 429 is a "wait", not a failure - retry the same message this many times. */
const RATE_LIMIT_RETRIES = 5;

/** Used only if a 429 arrives without any advertised wait. */
const RATE_LIMIT_FALLBACK_WAIT_MS = 1200;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** `apiFetch` falls back to this when a response carries no JSON error body. */
const BARE_STATUS_MESSAGE = /^Request failed \(\d+\)$/;

/**
 * Turns a transport/API error into copy a person can act on. The raw
 * "Request failed (429)" the API client falls back to is never shown.
 */
function describeSendError(error) {
  const status = error?.status;

  if (status === 429) {
    return 'The server is still asking us to slow down, so this message was not sent. Please try again in a moment.';
  }
  if (status === 401 || status === 403) {
    return 'You are not allowed to send messages in this conversation. Sign in again and retry.';
  }
  if (status === 404) {
    return 'This conversation is no longer available. Retry after reopening it.';
  }
  if (typeof status === 'number' && status >= 500) {
    return 'The server could not send that message. Please try again.';
  }
  if (typeof status === 'number' && status < 500) {
    // Validation copy from the API is already written for humans.
    return error.message && !BARE_STATUS_MESSAGE.test(error.message)
      ? error.message
      : 'That message was rejected. Please check it and retry.';
  }
  if (error instanceof TypeError) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  return error?.message
    ? `Could not send that message: ${error.message}`
    : 'Could not send that message. Please try again.';
}

/**
 * The human copy for a failed upload. Uploads have their own, much wider limit
 * (20 per hour per IP), so that one is named for what it is rather than being
 * reported as a generic send failure.
 */
function describeUploadError(error) {
  const status = error?.status;

  if (status === 429) {
    return 'Too many images have been uploaded from this connection just now. Please wait a moment and try again.';
  }
  if (status === 401 || status === 403) {
    return 'You are not allowed to upload images. Sign in again and retry.';
  }
  if (status === 413) {
    return 'That image is too large to upload. Please choose a smaller one.';
  }
  if (error instanceof TypeError) {
    return 'Could not reach the server to upload the image. Check your connection and retry.';
  }
  if (typeof status === 'number' && status >= 500) {
    return 'The server could not accept that image. Please try again.';
  }
  return error?.message && !BARE_STATUS_MESSAGE.test(error.message)
    ? `That image could not be uploaded: ${error.message}`
    : 'That image could not be uploaded. Please try again.';
}

/**
 * The open thread, backed by /api/chat/conversations/:id/messages.
 *
 * Real-time comes from the shared socket: the server broadcasts `new_message` to
 * both participants, including the sender, so an outgoing message is appended
 * from the socket as well as from the POST response - `appendUnique` dedupes by
 * id rather than trying to guess which arrives first.
 *
 * Only the /conversations/* family is used. The older /threads/* endpoints are
 * deliberately ignored (see the Phase 5 note in README).
 */
export function useMessages({ conversationId, socket, currentUserId }) {
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  /** `{ current, total }` while a multi-message send works through its queue. */
  const [progress, setProgress] = useState(null);

  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;

  /** When the last message POST was issued - what the server's window measures. */
  const lastPostAtRef = useRef(0);
  /** One batch at a time; the send button is disabled too, this is the backstop. */
  const inFlightRef = useRef(false);

  const appendUnique = useCallback((incoming) => {
    setMessages((current) =>
      current.some((message) => message.id === incoming.id) ? current : [...current, incoming],
    );
  }, []);

  const load = useCallback(
    async (id, { before } = {}) => {
      if (!id) {
        setMessages([]);
        setStatus('idle');
        return;
      }

      setStatus((current) => (current === 'ready' ? current : 'loading'));
      try {
        const query = new URLSearchParams({ limit: '50' });
        if (before) query.set('before', before);

        const data = await api.get(`/api/chat/conversations/${id}/messages?${query}`);
        const mapped = (data?.messages ?? []).map((row) => mapMessage(row, currentUserId));

        setMessages((current) => (before ? [...mapped, ...current] : mapped));
        setHasMore(Boolean(data?.hasMore));
        setCursor(data?.cursor ?? null);
        setStatus('ready');
        setError(null);

        // A thread is read the moment it is opened.
        api.post(`/api/chat/conversations/${id}/mark-read`).catch(() => {});
      } catch (requestError) {
        setError(requestError);
        setStatus('error');
      }
    },
    [currentUserId],
  );

  useEffect(() => {
    load(conversationId);
  }, [conversationId, load]);

  const loadOlder = useCallback(() => {
    if (!hasMore || !cursor || !conversationId) return;
    load(conversationId, { before: cursor });
  }, [hasMore, cursor, conversationId, load]);

  /* Append whatever the socket delivers for the open thread. */
  useEffect(() => {
    if (!socket) return undefined;

    return socket.subscribe((event) => {
      if (event.type !== 'new_message' || !event.message) return;
      const incoming = event.message;

      // Ignore traffic for other threads.
      if (incoming.conversationId !== conversationIdRef.current) return;

      appendUnique(mapMessage(incoming, currentUserId));

      // Reading the thread while a message lands marks it read straight away.
      if (incoming.senderId !== currentUserId) {
        api.post(`/api/chat/conversations/${incoming.conversationId}/mark-read`).catch(() => {});
      }
    });
  }, [socket, currentUserId, appendUnique]);

  /**
   * Waits out the server's 1 message/second window, measured from the last POST
   * this hook issued - a fixed window resets one second after it opens, so the
   * gap between two POSTs is what has to exceed it.
   */
  const waitForMessageSlot = useCallback(async () => {
    const waitMs = MESSAGE_MIN_GAP_MS - (Date.now() - lastPostAtRef.current);
    if (waitMs > 0) await sleep(waitMs);
  }, []);

  /**
   * One message POST, waiting out the server's window first and retrying while
   * the server is rate limiting us rather than failing the batch: a 429 means
   * "wait for the window", not "give up". `Retry-After`/`RateLimit-Reset`
   * (seconds) say how long to wait.
   *
   * `waitForMessageSlot` is a no-op whenever the previous message went out more
   * than a second ago - which is every ordinary send, so a lone message keeps
   * exactly the latency it had before. It only bites when messages are being
   * produced faster than the server accepts them: the 2nd..Nth attachment of a
   * batch, a message typed and sent within a second of the previous one, and a
   * retry. Requests the server would reject are never knowingly fired, so no
   * 429 ever reaches the console or the composer.
   */
  const postMessage = useCallback(
    async (id, payload) => {
      const url = `/api/chat/conversations/${id}/messages`;

      for (let attempt = 0; ; attempt += 1) {
        await waitForMessageSlot();

        lastPostAtRef.current = Date.now();

        try {
          return await api.post(url, payload);
        } catch (postError) {
          if (postError?.status !== 429 || attempt >= RATE_LIMIT_RETRIES) throw postError;

          const advertised = Number.isFinite(postError.retryAfterMs)
            ? postError.retryAfterMs
            : RATE_LIMIT_FALLBACK_WAIT_MS;
          // +150ms so the retry lands just past the window, never on its edge.
          await sleep(Math.max(advertised, 0) + 150);
        }
      }
    },
    [waitForMessageSlot],
  );

  /**
   * Sends one draft: the text, plus every attachment (the API takes one
   * `imageUrl` per message, so N attachments become N messages, first one
   * carrying the text - `body` is required, an image-only message sends ' ').
   *
   * Never throws for a send that simply failed. It resolves with a result the
   * composer can act on:
   *   `{ ok, sentCount, total, failedFiles, failedText, error }`
   * `failedFiles` are the attachments that still need sending (the one that
   * failed plus everything queued behind it), `failedText` is the draft text
   * when that never reached the server, and `error` carries human copy.
   * Rate limiting is absorbed here, so it never shows up as a failure.
   */
  const send = useCallback(
    async ({ text = '', files = [] }) => {
      const trimmed = text.trim();
      const total = Math.max(files.length, 1);

      // A second send while one is in flight is refused, not queued: the draft
      // stays intact and nothing is sent twice.
      if (!conversationId || (!trimmed && files.length === 0) || inFlightRef.current) {
        return {
          ok: false,
          skipped: true,
          sentCount: 0,
          total,
          failedFiles: [...files],
          failedText: trimmed,
          error: null,
        };
      }

      inFlightRef.current = true;
      setSending(true);
      setError(null);

      try {
        // 1. Upload attachments first - the message stores the returned URL.
        //    A failed upload aborts before anything is sent, so the whole draft
        //    (text included) survives for a retry.
        const urls = [];
        for (const file of files) {
          let uploaded;
          try {
            uploaded = await uploadChatImage(file);
          } catch (uploadError) {
            return {
              ok: false,
              sentCount: 0,
              total,
              failedFiles: [...files],
              failedText: trimmed,
              error: new Error(describeUploadError(uploadError)),
            };
          }
          if (!uploaded?.url) {
            return {
              ok: false,
              sentCount: 0,
              total,
              failedFiles: [...files],
              failedText: trimmed,
              error: new Error('That image could not be uploaded. Please try again.'),
            };
          }
          urls.push(uploaded.url);
        }

        // 2. The payload shape is unchanged: one imageUrl per message, text on
        //    the first, ' ' where a body is required but there is nothing to say.
        const payloads =
          urls.length > 0
            ? urls.map((imageUrl, index) => ({
                body: index === 0 ? trimmed || ' ' : ' ',
                imageUrl,
              }))
            : [{ body: trimmed }];

        // 3. Send them in order, pacing the batch against the server's window.
        let sentCount = 0;
        for (let index = 0; index < payloads.length; index += 1) {
          setProgress({ current: index + 1, total: payloads.length });

          let data;
          try {
            data = await postMessage(conversationId, payloads[index]);
          } catch (sendError) {
            // Stop here: later messages go without their text/order otherwise.
            // What did not go out stays in the composer.
            return {
              ok: false,
              sentCount,
              total: payloads.length,
              failedFiles: files.slice(index),
              failedText: index === 0 ? trimmed : '',
              error: new Error(describeSendError(sendError)),
            };
          }

          if (data?.message) appendUnique(mapMessage(data.message, currentUserId));
          sentCount += 1;
        }

        return {
          ok: true,
          sentCount,
          total: payloads.length,
          failedFiles: [],
          failedText: '',
          error: null,
        };
      } finally {
        setProgress(null);
        setSending(false);
        inFlightRef.current = false;
      }
    },
    [conversationId, currentUserId, appendUnique, postMessage],
  );

  return {
    messages,
    status,
    error,
    sending,
    progress,
    hasMore,
    loadOlder,
    send,
    reload: () => load(conversationId),
  };
}
